import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactCategory, ContactSubCategory } from 'src/Constants/ContactCategories.constants';
import { ContactKind, ContactPreviewDto } from 'src/Services/API/generated';
import { prettyPrintPhoneNumber } from 'src/Services/Formatters/prettyPrintPhoneNumber';
import { getCompanyRoleLabel } from 'src/Utils/companyContactRoles';
import { getContactName } from 'src/Utils/companyLogoDomain';

export interface ListedContact {
  id: string;
  name: string;
  kind: ContactKind;
  /** Company only: "Olivia Thevignot · Contact principal". */
  mainInterlocutorLabel?: string;
  /** Company only: where its logo is looked up. */
  websiteUrl?: string;
  logoEmails?: (string | undefined)[];
  phoneNumber?: string;
  isFavorite: boolean;
  isEmergencyContact: boolean;
  imageUrl?: string;
  familyRelationType?: string;
  category?: ContactCategory;
  subcategory?: ContactSubCategory;
}

export const useFormatListedContacts = (
  contacts?: ContactPreviewDto[]
): ListedContact[] | undefined => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!contacts) return undefined;

    const formattedContacts = contacts.map(contact => ({
      id: contact.id,
      name: getContactName(contact),
      kind: contact.kind ?? ContactKind.Person,
      websiteUrl: contact.websiteUrl,
      logoEmails: [contact.email],
      mainInterlocutorLabel: contact.mainInterlocutor?.displayName
        ? `${contact.mainInterlocutor.displayName} · ${getCompanyRoleLabel(
            t,
            contact.mainInterlocutor.companyRole,
            contact.mainInterlocutor.companyRoleLabel
          )}`
        : undefined,
      phoneNumber: contact.phoneNumber && prettyPrintPhoneNumber(contact.phoneNumber),
      isFavorite: contact.isFavorite,
      isEmergencyContact: contact.isEmergencyContact,
      imageUrl: contact.imageUrl,
      familyRelationType: contact.familyRelationType,
      category: contact.category,
      subcategory: contact.subcategory,
    }));

    return formattedContacts;
  }, [contacts, t]);
};
